import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    // Basic email validation
    if (!email.endsWith('@cab444.com')) {
      setError('Please use your CAB444 university email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://0oto2a7yqe.execute-api.ap-southeast-2.amazonaws.com/prod/auth/login', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(), 
          password: password 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Validate response data
      if (!data.idToken || !data.role) {
        throw new Error('Invalid response from server');
      }

      // Store tokens securely
      sessionStorage.setItem('idToken', data.idToken);
      sessionStorage.setItem('accessToken', data.accessToken);
      sessionStorage.setItem('userEmail', data.email);
      sessionStorage.setItem('userRole', data.role);

      // Force page reload to update auth context
      window.location.href = data.role === 'Admin' ? '/admin' : '/dashboard';

    } catch (err: any) {
      console.error('Login error:', err);
      
      // Handle network errors
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Unable to connect to login service. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setEmail('');
    setPassword('');
    setError('');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #003A70 0%, #0066CC 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        border: '3px solid #FF6600',
        overflow: 'hidden',
        maxWidth: '420px',
        width: '100%'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #003A70 0%, #0066CC 100%)',
          padding: '2rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: 'white',
            borderRadius: '50%',
            margin: '0 auto 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#003A70'
          }}>
            QUT
          </div>
          <h1 style={{ 
            margin: '0 0 0.5rem 0', 
            fontSize: '1.8rem',
            fontWeight: '600'
          }}>
            CAB444 Portal
          </h1>
          <p style={{ 
            margin: 0, 
            opacity: 0.9,
            fontSize: '1rem'
          }}>
            Queensland University of Technology
          </p>
        </div>
        
        <div style={{ padding: '2rem' }}>

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              color: '#333',
              fontWeight: '500',
              fontSize: '0.9rem'
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem',
                border: '2px solid #e1e5e9',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF6600'}
              onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              placeholder="name@cab444.com"
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              color: '#333',
              fontWeight: '500',
              fontSize: '0.9rem'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem',
                border: '2px solid #e1e5e9',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF6600'}
              onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          {error && (
            <div style={{
              color: '#dc3545',
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#FFE6CC',
              border: '1px solid #FF6600',
              borderRadius: '8px',
              fontSize: '0.9rem',
              lineHeight: '1.4'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                ⚠️ Login Error
              </div>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '1rem',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #003A70 0%, #0066CC 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(0,102,204,0.3)'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                const target = e.target as HTMLButtonElement;
                target.style.transform = 'translateY(-1px)';
                target.style.boxShadow = '0 6px 16px rgba(0,102,204,0.4)';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                const target = e.target as HTMLButtonElement;
                target.style.transform = 'translateY(0)';
                target.style.boxShadow = '0 4px 12px rgba(0,102,204,0.3)';
              }
            }}
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ 
                  marginRight: '0.5rem',
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block'
                }}>⟳</span>
                Signing in...
              </span>
            ) : 'Sign In'}
          </button>
        </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
